import React from 'react';
import { Box, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';

import { NoiseCancellationEditor } from './NoiseCancellationEditor';
import { CampaignNoiseCancellationDraft } from '../utils/campaignNoiseCancellation.utils';
import { NoiseCancellationValidation } from '../utils/noiseProcessing.utils';

type CampaignNoiseCancellationFormProps = {
  value: CampaignNoiseCancellationDraft | null;
  onChange: (value: CampaignNoiseCancellationDraft) => void;
  errors?: NoiseCancellationValidation;
  disabled?: boolean;
  showEnabledToggle?: boolean;
};

export const CampaignNoiseCancellationForm: React.FC<CampaignNoiseCancellationFormProps> = ({
  value,
  onChange,
  errors = {},
  disabled = false,
  showEnabledToggle = true,
}) => {
  const { t } = useTranslation();

  if (!value) return null;

  const isEnabled = value.enabled;
  const updateEnabled = (enabled: boolean) =>
    onChange({
      ...value,
      enabled,
      configured: enabled ? true : value.configured,
    });

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}>
      {showEnabledToggle ? (
        <FormControlLabel
          control={
            <CustomSwitch
              checked={isEnabled}
              disabled={disabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                updateEnabled(event.target.checked)
              }
            />
          }
          label={t('noiseProcessing.cancellation.enabled')}
          sx={{ ml: 0, gap: 1 }}
        />
      ) : null}
      {errors.enabled ? (
        <Typography variant="body2" color="error.main" role="alert">
          {t(errors.enabled)}
        </Typography>
      ) : null}
      <NoiseCancellationEditor
        value={value}
        onChange={(nextValue) => onChange({ ...value, ...nextValue })}
        errors={errors}
        disabled={disabled || !isEnabled}
        warning={null}
        showConfiguredToggle={false}
      />
    </Box>
  );
};
